// React
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import React from "react"

// Hooks and Redux
import { useAppSelector } from "../../../redux/hooks"

// MUI Components
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Icon from "@mui/material/Icon"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"

import {
  DataGrid,
  enUS,
  GridCellParams,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from "@mui/x-data-grid"

// Utils
import { useTranslation } from "react-i18next"
import { buildMenuActiveMap } from "../../../utils"

// Types
import theme from "../../../styles/theme"
import { thTHGrid } from "../../../@types/DataGridLocaleText"
import { IBank } from "../../../@types"

// Services
import { getAllActiveBank } from "../../../services/MasterDataService"

const Banks = () => {
  const { t, i18n } = useTranslation()
  const userRoleType = useAppSelector((state) => state.user.roletype)
  const navigate = useNavigate()
  // const { menu } = useAppSelector((state) => state.user)
  // const { menus: allMenus } = useAppSelector((state) => state.masterData)

  const [bankLoading, setBankLoading] = useState<boolean>(true)
  const [bankData, setBankData] = useState<IBank[]>([])

  const allSuccessLoading = !bankLoading

  // useEffect(() => {
  //   console.log("menu  ", menu)
  //   console.log("allMenus  ", allMenus)
  //   const activeMap = buildMenuActiveMap(allMenus)
  //   console.log("allMenus  ", activeMap)
  //   // if (!allMenus?.menus) return

  //   // const hasPermission = menu.includes(currentMenuId)   // อยู่ในสิทธิ์ไหม
  //   // const isActive = activeMap[currentMenuId] === true    // เมนูเปิดใช้งานไหม

  //   // if (!hasPermission || !isActive) {
  //   //   // navigate("/403", { replace: true })
  //   // }
  // }, [allMenus])

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setBankLoading(true)
        const response = await getAllActiveBank("All")
        const data = await response.data

        if (data.success) {
          setBankData(data.banks)
        } else {
          setBankData([])
        }
      } catch (error) {
        navigate("/servererror", { replace: true })
      } finally {
        setTimeout(() => {
          setBankLoading(false)
        }, 500)
      }
    }

    fetchBanks()
  }, [navigate])

  // NOTE: CustomToolbar — ซ้ำกันทุกหน้าที่มี DataGrid (37+ ไฟล์) ควรย้ายไปรวมใน components/common/DataGridToolbar.tsx
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    )
  }

  const columns: GridColDef[] = [
    {
      field: "edit",
      headerName: `${t("Edit")}`,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      width: 80,
      renderCell: (params) => {
        if (userRoleType === "Super Admin") {
          return (
            <Link
              to={`/admin/masterdata/bank/edit/${params.row.bank_code}`}
              state={{ bankData: params.row }}
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                color: theme.palette.button?.main ?? "#0288d1",
              }}
            >
              <Tooltip title={t("Edit")}>
                <Icon fontSize="medium">edit</Icon>
              </Tooltip>
            </Link>
          )
        }
        return null
      },
    },
    {
      field: "is_active",
      headerName: `${t("Status")}`,
      width: 120,
      hideSortIcons: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      renderCell: (params: GridCellParams) =>
        params.row.is_active === true ? (
          <Chip
            variant="outlined"
            color="success"
            label={`${t("Active")}`}
            size="small"
          />
        ) : (
          <Chip
            variant="outlined"
            color="error"
            label={`${t("Inactive")}`}
            size="small"
          />
        ),
    },
    {
      field: "bank_code",
      headerName: `${t("Bank Code")}`,
      width: 150,
      valueGetter: (params: GridCellParams) => params.row.bank_code,
    },
    {
      field: "bank_name",
      headerName: `${t("Bank Name")}`,
      width: 250,
      valueGetter: (params: GridCellParams) => params.row.bank_name,
    },
  ]

  return (
    <>
      <div>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "70px",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", textAlign: "left" }}
          >
            {t("MasterData Bank")}
          </Typography>
          {userRoleType === "Super Admin" && (
            <Button
              variant="contained"
              disableElevation
              sx={{
                backgroundColor: theme.palette.button?.main ?? "#0288d1",
                color: "white",
                "&:hover": { backgroundColor: "#0277bd" },
                minWidth: "fit-content",
                minHeight: "42px",
              }}
              component={Link}
              to="/admin/masterdata/bank/create"
            >
              {t("Create New Bank")}
            </Button>
          )}
        </Box>

        <Box sx={{ width: "100%", paddingX: 3, mt: 3 }}>
          {!allSuccessLoading ? (
            <Box display="flex" justifyContent="center" gap={2}>
              <CircularProgress color="success" size={20} />
              <Typography align="center">{t("Loading")}...</Typography>
            </Box>
          ) : bankData.length !== 0 ? (
            <Box display="inline-grid" sx={{ width: "100%" }}>
              <DataGrid
                getRowId={(row) => row.bank_code}
                rows={bankData}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 10 },
                  },
                }}
                pagination
                pageSizeOptions={[10, 20, 30, 40, 50]}
                getRowHeight={() => 50}
                disableRowSelectionOnClick
                sx={{
                  "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
                    {
                      outline: "none !important",
                    },
                  fontSize: 13,
                }}
                slots={{ toolbar: CustomToolbar }}
                slotProps={{
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: {
                        variant: "outlined",
                        size: "small",
                      },
                      columnInputProps: {
                        variant: "outlined",
                        size: "small",
                        sx: { mt: "auto" },
                      },
                      operatorInputProps: {
                        variant: "outlined",
                        size: "small",
                        sx: { mt: "auto" },
                      },
                      valueInputProps: {
                        InputComponentProps: {
                          variant: "outlined",
                          size: "small",
                        },
                      },
                      deleteIconProps: {
                        sx: { "& .MuiSvgIcon-root": { color: "#d32f2f" } },
                      },
                    },
                    sx: {
                      "& .MuiDataGrid-filterForm": { p: 2 },
                      "& .MuiDataGrid-filterFormLogicOperatorInput": { mr: 2 },
                      "& .MuiDataGrid-filterFormColumnInput": {
                        mr: 2,
                        width: 150,
                      },
                      "& .MuiDataGrid-filterFormOperatorInput": { mr: 2 },
                      "& .MuiDataGrid-filterFormValueInput": { width: 200 },
                    },
                  },
                }}
                localeText={
                  i18n.language === "en"
                    ? enUS.components.MuiDataGrid.defaultProps.localeText
                    : thTHGrid
                }
              />
            </Box>
          ) : (
            <Typography align="center">{t("No data")}</Typography>
          )}
        </Box>
      </div>
    </>
  )
}

export default Banks

 